import React, { useState, useEffect } from 'react';
import productAPI from '../../services/productApi';
import { useCart } from '../../contexts/CartContext';
import './ProductComparison.css';

const ProductComparison = ({ pageData, setCurrentPage, setPageData }) => {
    const { addToCart } = useCart();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadComparisonData = async () => {
            setLoading(true);
            setError(null);

            try {
                if (pageData && pageData.products && pageData.products.length > 0) {
                    // If we already have product data passed in
                    setProducts(pageData.products);
                } else if (pageData && pageData.productIds && pageData.productIds.length > 0) {
                    // If we only have IDs, fetch the products
                    const fetchedProducts = await Promise.all(
                        pageData.productIds.map(async (id) => {
                            const response = await productAPI.getProduct(id);
                            if (response.success || response.data) {
                                const productData = response.data?.product || response.data || response;
                                return productAPI.processItemImages ? productAPI.processItemImages(productData) : productData;
                            }
                            return null;
                        })
                    );
                    setProducts(fetchedProducts.filter(p => p !== null));
                } else {
                    setError('No products selected for comparison.');
                }
            } catch (err) {
                console.error('Error loading comparison data:', err);
                setError('Failed to load product details for comparison.');
            } finally {
                setLoading(false);
            }
        };

        loadComparisonData();
    }, [pageData]);

    const handleBack = () => {
        setCurrentPage('products');
    };

    const handleAddToCart = (product) => {
        addToCart({
            id: product.id,
            title: product.name || product.title,
            price: product.price,
            image: product.image,
            type: 'product',
            description: product.description
        }, 1);
        // Optional: Show success message
    };

    if (loading) {
        return (
            <div className="comparison-page loading">
                <div className="container">
                    <h2>Loading Comparison...</h2>
                </div>
            </div>
        );
    }

    if (error || products.length === 0) {
        return (
            <div className="comparison-page error">
                <div className="container">
                    <h2>Comparison Error</h2>
                    <p>{error || 'No products found to compare.'}</p>
                    <button className="btn btn-primary" onClick={handleBack}>Back to Products</button>
                </div>
            </div>
        );
    }

    // Helper to safely get nested properties or default to '-'
    const getValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj) || '-';
    };

    // Define comparison fields
    // This could be dynamic based on category, but starting with a generic set
    const comparisonFields = [
        { label: 'Price', key: 'price', format: (val) => `$${Number(val).toFixed(2)}` },
        { label: 'Category', key: 'category.name' }, // Assuming category is an object with name
        { label: 'Stock Status', key: 'stock_quantity', format: (val) => val > 0 ? 'In Stock' : 'Out of Stock' },
        { label: 'Description', key: 'description', className: 'description-cell' },
        // Add more specific fields if available in 'features' or other properties
    ];

    // Extract all unique feature keys from both products to create dynamic rows
    const allFeatureKeys = Array.from(new Set(
        products.flatMap(p => p.features ? p.features.map(f => f.name || f.key) : [])
    ));

    return (
        <div className="comparison-page">
            <div className="container">
                <div className="comparison-header">
                    <button className="btn btn-secondary back-btn" onClick={handleBack}>
                        ← Back to Products
                    </button>
                    <h1>Product Comparison</h1>
                </div>

                <div className="comparison-table-wrapper">
                    <table className="comparison-table">
                        <thead>
                            <tr>
                                <th>Feature</th>
                                {products.map(product => (
                                    <th key={product.id} className="product-header">
                                        <div className="product-header-content">
                                            <img
                                                src={product.image || product.primary_image || '/images/placeholder.jpg'}
                                                alt={product.name || product.title}
                                                className="product-image"
                                            />
                                            <h3>{product.name || product.title}</h3>
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={() => handleAddToCart(product)}
                                            >
                                                Add to Cart
                                            </button>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Basic Fields */}
                            {comparisonFields.map(field => (
                                <tr key={field.key}>
                                    <td className="feature-label">{field.label}</td>
                                    {products.map(product => (
                                        <td key={product.id} className={field.className || ''}>
                                            {field.format ? field.format(getValue(product, field.key)) : getValue(product, field.key)}
                                        </td>
                                    ))}
                                </tr>
                            ))}

                            {/* Dynamic Features from 'features' array */}
                            {allFeatureKeys.length > 0 && (
                                <>
                                    <tr className="section-header">
                                        <td colSpan={products.length + 1}>Specifications</td>
                                    </tr>
                                    {allFeatureKeys.map(featureName => (
                                        <tr key={featureName}>
                                            <td className="feature-label">{featureName}</td>
                                            {products.map(product => {
                                                const feature = product.features?.find(f => (f.name === featureName || f.key === featureName));
                                                return (
                                                    <td key={product.id}>
                                                        {feature ? (feature.value || feature.description || 'Yes') : '-'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProductComparison;
